import { Loader2, PanelLeft, RefreshCw, X } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { LibraryBrowser } from "@/components/library/library-browser"
import { ItemMedia, type LibraryItem } from "@/components/library/library-item"
import type { LibraryPickContextValue } from "@/components/library/library-items"
import { Button } from "@/components/ui/primitives"
import { SearchableCombobox } from "@/components/ui/searchable-combobox"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { normalizeLibrarySelection, useLibraryBrowser } from "@/hooks/use-library-browser"
import { libraryPickResult, useLibraryPickSelection, type LibraryPickResult } from "@/hooks/use-library-pick-selection"
import { useTranslation } from "@/hooks/use-translation"
import { fetchLibraryTree, readCachedLibraryTree, type LibraryTreePayload } from "@/lib/library-api"
import {
  libraryItemUnpickable,
  libraryNeedMediaKinds,
  librarySourceKey,
  type LibraryPickMedia,
  type LibraryUnpickableReason,
} from "@/lib/library-source"
import { cn } from "@/lib/utils"
import type { ShotFrameSource } from "@/lib/workspace-api"

/**
 * Назначение как ДАННЫЕ: модалка не знает ни одного конкретного слота, поэтому новая
 * точка выбора — это ещё один элемент массива у вызывающего, а не ветка внутри диалога.
 */
export type LibraryPickTarget = {
  id: string
  label: string
  /** Какие типы медиа принимает: несовместимые ассеты гаснут до клика, а не отдают 422 после. */
  accepts: readonly LibraryPickMedia[]
  /** Непусто — назначение недоступно (например, опорник у шота-перехода). */
  disabledReason?: string | null
}

/** Стабильная ссылка: дефолт-литерал в пропсах пересоздавал бы мемо каждый рендер. */
const EMPTY_PICKED_KEYS: readonly string[] = []

export type LibraryProjectChoice = {
  id: number
  name: string
  frameFormat: string
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null — проект ещё не выбран: вместо дерева комбобокс projectChoices. */
  projectId: number | null
  /** Список, из которого выбирают проект, пока projectId пуст. */
  projectChoices?: readonly LibraryProjectChoice[]
  projectChoicesLoading?: boolean
  onProjectChange?: (projectId: number) => void
  frameFormat: string
  frameRate?: number
  /** Папка, в которой модалка открывается: обычно шот, для которого выбирают кадр. */
  initialShotId?: number | null
  /** Доска Холста как стартовая папка — побеждает initialShotId. У Холста «своего шота» нет. */
  initialCanvasId?: number | null
  /** Узел «Аниматик» эпизода как стартовая папка — для привязки сегмента; побеждает остальные. */
  initialAnimaticEpisodeId?: number | null
  /** Секция «Превизы» / 3D-сцены как стартовая папка — для привязки превиза к шоту. */
  initialPrevizes?: boolean
  /** Уже прикреплённые ассеты (ключи librarySourceKey): плитка гаснет вместо тихого no-op. */
  pickedKeys?: readonly string[]
  /**
   * Множественный выбор. Клик по ассету открывает просмотрщик; в набор ассет попадает
   * только чекбоксом. Подтверждает кнопка назначения внизу.
   */
  multiple?: boolean
  /** Сколько ассетов можно набрать за раз; без значения — без предела. */
  selectionLimit?: number
  /**
   * Подтверждение набора. Приходит только то, что принимает нажатое назначение.
   * Модалку закрывает вызывающий: диалог набор не чистит и сам не закрывается,
   * поэтому при частичном отказе выбор остаётся на экране.
   */
  onPickMany?: (targetId: string, picked: readonly LibraryPickResult[]) => void
  targets: LibraryPickTarget[]
  /**
   * Оставлен для вызывающих. Назначает только кнопка внизу: Enter и двойной клик
   * открывают ассет и этим пропом не пользуются.
   */
  defaultTargetId?: string
  /** Идёт назначение: спиннер только на нажатой кнопке, сетка не блокируется. */
  busyTargetId?: string | null
  /**
   * Одиночный выбор. Закрывает модалку сам вызывающий и только после успеха: при ошибке
   * пикер остаётся открытым с сохранённым выбором, чтобы можно было взять другой кадр.
   * item отдаётся третьим аргументом — Холсту нужны label/url/тип, чтобы нарисовать
   * плитку референса до генерации; существующие потребители его просто игнорируют.
   */
  onPick: (targetId: string, source: ShotFrameSource, item: LibraryItem) => void
  /** Что назначаем и куда — уходит в подзаголовок диалога. */
  subtitle?: string
}

export function LibraryPickerDialog(props: Props) {
  const {
    open,
    onOpenChange,
    projectId,
    projectChoices = [],
    projectChoicesLoading = false,
    onProjectChange,
    subtitle,
  } = props
  const { t } = useTranslation(["workspace", "common"])
  const [payload, setPayload] = useState<LibraryTreePayload | null>(null)
  const [failed, setFailed] = useState(false)

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (projectId == null) return
      setFailed(false)
      // Кэш рисует дерево мгновенно, но с прошлого открытия могли появиться сцены,
      // шоты и доски — поэтому свежее дерево запрашиваем всегда. Ошибку показываем
      // только когда показать нечего: со старым деревом работать всё ещё можно.
      // Пустой кэш другого проекта сбрасывает, иначе на экране останется чужое дерево.
      const cached = readCachedLibraryTree(projectId)
      setPayload(cached)
      fetchLibraryTree(projectId, { signal, force: true })
        .then((tree) => {
          if (!signal?.aborted) setPayload(tree)
        })
        .catch(() => {
          if (!signal?.aborted && !cached) setFailed(true)
        })
    },
    [projectId],
  )

  useEffect(() => {
    if (!open || projectId == null) return
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [open, load, projectId])

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      nested
      size="full"
      title={t("library.title")}
      description={subtitle ?? t("library.pick.description")}
      inlineDescription
      closeAriaLabel={t("common:close")}
      maxHeightClassName="h-[min(88vh,44rem)]"
      contentClassName="overflow-hidden"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
      onOpenAutoFocus={(e) => {
        // Radix фокусирует крестик — он ловит Enter вместо подтверждения выбора.
        e.preventDefault()
      }}
    >
      {projectId == null ? (
        <LibraryProjectChooser
          choices={projectChoices}
          loading={projectChoicesLoading}
          onChange={(id) => onProjectChange?.(id)}
        />
      ) : payload == null ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-sm text-ui-text-secondary">
          {failed ? (
            <>
              <p className="text-ui-status-failed">{t("frameLibrary.loadFailed")}</p>
              <Button type="button" size="sm" variant="outline" onClick={() => load()}>
                <RefreshCw className="h-3.5 w-3.5" />
                {t("canvas.panel.libraryRetry")}
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("canvas.panel.libraryLoading")}
            </>
          )}
        </div>
      ) : (
        // key по проекту: смена проекта пересоздаёт браузер с новой стартовой папкой.
        <LibraryPickerBody key={projectId} {...props} projectId={projectId} payload={payload} />
      )}
    </WorkspaceDialog>
  )
}

function LibraryProjectChooser({
  choices,
  loading,
  onChange,
}: {
  choices: readonly LibraryProjectChoice[]
  loading: boolean
  onChange: (projectId: number) => void
}) {
  const { t } = useTranslation("workspace")
  if (loading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-sm text-ui-text-secondary">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("canvas.panel.libraryLoading")}
      </div>
    )
  }
  if (choices.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-10 text-sm text-ui-text-secondary">
        {t("library.pick.noProjects")}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10">
      <p className="text-sm text-ui-text-secondary">{t("library.pick.chooseProject")}</p>
      <SearchableCombobox
        className="w-full max-w-sm"
        value=""
        onChange={(value) => {
          const id = Number(value)
          if (Number.isInteger(id) && id > 0) onChange(id)
        }}
        options={choices.map((project) => ({ value: String(project.id), label: project.name }))}
        placeholder={t("library.pick.chooseProject")}
        ariaLabel={t("library.pick.chooseProject")}
        emptyLabel={t("library.pick.noProjects")}
        portalToBody
        size="md"
      />
    </div>
  )
}

function LibraryPickerBody({
  projectId,
  frameFormat,
  frameRate,
  initialShotId = null,
  initialCanvasId = null,
  initialAnimaticEpisodeId = null,
  initialPrevizes = false,
  pickedKeys = EMPTY_PICKED_KEYS,
  multiple = false,
  selectionLimit,
  onPickMany,
  targets,
  busyTargetId = null,
  onPick,
  onOpenChange,
  payload,
}: Omit<Props, "projectId"> & { projectId: number; payload: LibraryTreePayload }) {
  const { t } = useTranslation(["workspace", "common"])
  const rootRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [treeHidden, setTreeHidden] = useState(false)

  const [initialSelection] = useState(() =>
    normalizeLibrarySelection(
      // Открываемся сразу в папке, из которой пришли: у шота это его собственная папка,
      // у Холста — его доска (там и генерации доски, и её загрузки), при привязке
      // сегмента — узел «Аниматик» эпизода. Дерево для этого разворачивать не нужно.
      initialPrevizes
        ? {
            section: "scenes_3d",
            entityId: null,
          }
        : initialAnimaticEpisodeId != null
          ? { section: "shots", node: { level: "animatic", episodeId: initialAnimaticEpisodeId } }
          : initialCanvasId != null
            ? { section: "canvas", canvasId: initialCanvasId }
            : initialShotId != null
              ? { section: "shots", node: { level: "shot", shotId: initialShotId } }
              : null,
      payload.tree,
      payload.canvases,
      payload.active_episode_id,
      payload.entities,
      payload.flows ?? [],
    ),
  )

  const activeTargets = useMemo(() => targets.filter((target) => !target.disabledReason), [targets])

  const selection = useLibraryPickSelection({ limit: selectionLimit })
  // Одиночный режим хранит сам ассет, не ключ под курсором: уйдя в другую папку,
  // превью и подпись внизу должны остаться.
  const [checked, setChecked] = useState<LibraryItem | null>(null)

  const browser = useLibraryBrowser({
    tree: payload.tree,
    canvases: payload.canvases,
    flows: payload.flows ?? [],
    entities: payload.entities,
    activeEpisodeId: payload.active_episode_id,
    initialSelection,
    rootRef,
    contentRef,
  })

  const mediaLabels = useMemo<Record<LibraryPickMedia, string>>(
    () => ({
      image: t("library.pick.media.image"),
      video: t("library.pick.media.video"),
      audio: t("library.pick.media.audio"),
      animatic: t("library.pick.media.animatic"),
      shot_animatic: t("library.pick.media.shot_animatic"),
      previz: t("library.pick.media.previz"),
    }),
    [t],
  )

  // Отказ по типу медиа перечисляет, что сюда можно: «нужно изображение или видео»
  // понятнее, чем «нельзя», и не врёт, когда слот принимает несколько типов.
  const reasonText = useCallback(
    (item: LibraryItem, reason: LibraryUnpickableReason, accepts: readonly LibraryPickMedia[]) => {
      if (reason === "notReady") return t("library.pick.reason.notReady")
      if (reason === "noSource") return t("library.pick.reason.noSource")
      if (reason === "locked") return item.lockedReason ?? ""
      return t("library.pick.reason.needMedia", {
        kinds: libraryNeedMediaKinds(accepts)
          .map((media) => mediaLabels[media])
          .join(t("library.pick.media.or")),
      })
    },
    [t, mediaLabels],
  )

  // Плитку гасим, только если её нельзя назначить НИ ОДНИМ из назначений: иначе при
  // нескольких слотах половина библиотеки выглядела бы недоступной без причины.
  const alreadyPicked = useCallback(
    (item: LibraryItem) => item.source != null && pickedKeys.includes(librarySourceKey(item.source)),
    [pickedKeys],
  )

  const inSelection = useCallback(
    (item: LibraryItem) => item.source != null && selection.has(librarySourceKey(item.source)),
    [selection],
  )

  const unpickable = useCallback(
    (item: LibraryItem) => {
      if (alreadyPicked(item)) return t("library.pick.reason.alreadyPicked")
      // Лимит гасит только то, что ещё не выбрано: снять выбор можно всегда.
      if (multiple && selection.full && !inSelection(item)) {
        return t("library.pick.reason.limitReached", { count: selectionLimit ?? 0 })
      }
      const blocked = activeTargets.map((target) => ({
        reason: libraryItemUnpickable(item, target.accepts),
        accepts: target.accepts,
      }))
      if (blocked.length === 0 || blocked.some((entry) => entry.reason == null)) return null
      return reasonText(item, blocked[0].reason as LibraryUnpickableReason, blocked[0].accepts)
    },
    [activeTargets, reasonText, alreadyPicked, t, multiple, selection.full, inSelection, selectionLimit],
  )

  const toggleChecked = useCallback(
    (item: LibraryItem) => {
      if (!item.source) return
      const key = librarySourceKey(item.source)
      const marked = multiple ? selection.has(key) : checked?.source != null && librarySourceKey(checked.source) === key
      // Недоступный ассет нельзя отметить, но снять отметку можно всегда:
      // иначе смена вкладки или лимит запирали бы уже набранное.
      if (unpickable(item) != null && !marked) return
      if (multiple) {
        const result = libraryPickResult(item, item.source)
        if (selection.full && !selection.has(result.key)) return
        selection.toggle(result)
        return
      }
      setChecked((current) => (current?.source != null && librarySourceKey(current.source) === key ? null : item))
    },
    [checked, multiple, selection, unpickable],
  )

  // Сначала снимаем строки, вышедшие из куска: addMany увидит освободившиеся места.
  // Лимит здесь не спрашиваем — unpickable ещё видит набор до снятия.
  const toggleRange = useCallback(
    (selectItems: LibraryItem[], dropItems: LibraryItem[]) => {
      for (const item of dropItems) {
        if (!item.source) continue
        selection.remove(librarySourceKey(item.source))
      }
      const results: LibraryPickResult[] = []
      for (const item of selectItems) {
        if (!item.source) continue
        const sourceKey = librarySourceKey(item.source)
        if (selection.has(sourceKey)) continue
        if (alreadyPicked(item)) continue
        const blocked = activeTargets.map((target) => libraryItemUnpickable(item, target.accepts))
        if (blocked.length > 0 && blocked.every((reason) => reason != null)) continue
        results.push(libraryPickResult(item, item.source))
      }
      selection.addMany(results)
    },
    [selection, alreadyPicked, activeTargets],
  )

  const pick: LibraryPickContextValue = {
    unpickable,
    // Сравниваем источник, а не key плитки: ключ «storyboard» есть у каждого шота.
    selected: (item) => {
      if (item.source == null) return false
      const key = librarySourceKey(item.source)
      if (multiple) return selection.has(key)
      return checked?.source != null && librarySourceKey(checked.source) === key
    },
    onToggle: toggleChecked,
    onToggleRange: multiple ? toggleRange : undefined,
    toggleLabel: t("library.pick.toggle"),
  }

  const targetReason = (target: LibraryPickTarget) => {
    if (target.disabledReason) return target.disabledReason
    if (!checked?.source) return null
    if (alreadyPicked(checked)) return t("library.pick.reason.alreadyPicked")
    const reason = libraryItemUnpickable(checked, target.accepts)
    return reason ? reasonText(checked, reason, target.accepts) : null
  }

  // Показываем причину, только когда выбранный ассет не подходит НИ ОДНОМУ назначению:
  // при нескольких слотах «нельзя вот сюда» — это не ошибка, а обычное состояние.
  const reasons = checked ? targets.map(targetReason) : []
  const blockingReason = reasons.length > 0 && reasons.every(Boolean) ? reasons[0] : null

  return (
    <>
      <LibraryBrowser
        browser={browser}
        projectId={projectId}
        tree={payload.tree}
        canvases={payload.canvases}
        flows={payload.flows ?? []}
        entities={payload.entities}
        sketches={payload.sketches ?? { count: 0 }}
        uploads={payload.uploads ?? { count: 0 }}
        activeEpisodeId={payload.active_episode_id}
        frameFormat={frameFormat}
        frameRate={frameRate}
        variant="dialog"
        rootRef={rootRef}
        contentRef={contentRef}
        treeHidden={treeHidden}
        pick={pick}
        toolbarStart={
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="hidden shrink-0 sm:inline-flex"
            aria-pressed={!treeHidden}
            onClick={() => setTreeHidden((v) => !v)}
            title={t("library.pick.toggleTree")}
            aria-label={t("library.pick.toggleTree")}
          >
            <PanelLeft className={cn("h-3.5 w-3.5", !treeHidden && "text-ui-accent-text")} />
          </Button>
        }
      />

      <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-ui-border p-3">
        {/* min-h-10 — высота одного ряда выбора (превью h-8 и вынос крестика).
            Пустая подпись её не схлопывает. Выше — только если кнопки ушли на вторую строку. */}
        <div className="flex min-h-10 min-w-0 flex-1 items-center gap-2">
          {multiple ? (
            selection.count === 0 ? (
              <span className="text-xs text-ui-text-muted">{t("library.pick.nothingSelected")}</span>
            ) : (
              <>
                <span className="shrink-0 text-xs text-ui-text-secondary tabular-nums">
                  {selectionLimit != null
                    ? t("library.pick.selectedOfLimit", { count: selection.count, limit: selectionLimit })
                    : t("library.pick.selectedCount", { count: selection.count })}
                </span>
                {/* py-1 обязателен: крестики вынесены на -top-1, а overflow-x режет и по вертикали. */}
                <div className="it2tv-scrollbar flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto py-1">
                  {selection.picked.map((entry) => (
                    <span key={entry.key} className="group relative shrink-0" title={entry.label}>
                      <span className="block h-8 w-12 overflow-hidden rounded bg-ui-surface-sunken">
                        {entry.thumbUrl ? (
                          <img src={entry.thumbUrl} alt="" className="h-full w-full object-cover" />
                        ) : null}
                      </span>
                      <button
                        type="button"
                        onClick={() => selection.remove(entry.key)}
                        aria-label={t("library.pick.removeFromSelection", { label: entry.label })}
                        className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--ui-chip-bg-strong)] text-ui-text-primary backdrop-blur-sm"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </>
            )
          ) : checked ? (
            <>
              <ItemMedia item={checked} className="h-8 max-h-8 w-12 shrink-0 rounded" />
              <span className="flex min-w-0 flex-col leading-4">
                <span className="truncate text-xs text-ui-text-secondary" title={checked.label}>
                  {checked.label}
                </span>
                {/* Причина отказа — текстом рядом с выбором: в title у выключенной кнопки её не видно. */}
                {blockingReason ? (
                  <span className="truncate text-[11px] text-ui-status-failed">{blockingReason}</span>
                ) : null}
              </span>
            </>
          ) : (
            <span className="text-xs text-ui-text-muted">{t("library.pick.nothingSelected")}</span>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common:cancel")}
          </Button>
          {targets.map((target) => {
            // Набор сверяем с accepts НАЖАТОЙ кнопки, а не только в момент тоггла:
            // пока модалка открыта, вкладка Изображение/Видео могла смениться, и
            // набранное видео стало бы незаконным уже после выбора.
            const compatible = multiple ? selection.picked.filter((entry) => target.accepts.includes(entry.media)) : []
            const reason = multiple ? target.disabledReason : targetReason(target)
            const disabled = multiple
              ? compatible.length === 0 || reason != null || busyTargetId != null
              : !checked?.source || reason != null || busyTargetId != null
            return (
              <Button
                key={target.id}
                type="button"
                size="sm"
                disabled={disabled}
                title={reason ?? undefined}
                onClick={() => {
                  if (multiple) {
                    onPickMany?.(target.id, compatible)
                    return
                  }
                  if (!checked?.source || busyTargetId != null) return
                  if (libraryItemUnpickable(checked, target.accepts) != null) return
                  if (pickedKeys.includes(librarySourceKey(checked.source))) return
                  onPick(target.id, checked.source, checked)
                }}
              >
                {busyTargetId === target.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {target.label}
              </Button>
            )
          })}
        </div>
      </div>
    </>
  )
}
